import CloudDownloadOutlinedIcon from '@mui/icons-material/CloudDownloadOutlined';
import FilterIcon from '@mui/icons-material/TuneOutlined';
import { useTheme } from '@mui/material';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import { useTranslation } from 'react-i18next';

import Button from '@/components/Button';
import GenericDrawer from '@/components/GenericDrawer';
import { AgaveDataGrid } from '@/components/GenericTable/DataGridStyled';
import DetailsModal from '@/components/Modals/DetailsModal/DetailsModal';
import Modal from '@/components/Modals/Modal';
import SearchInput from '@/components/SearchInput';

import DownloadModal from '../dependencies/DownloadModal/DownloadModal';
import Filters from './Filters';
import { orderStatusColumns } from './orderStatus-columns';
import useOrderStatus from './useOrderStatus';
import { filters } from './utils';

const OrderStatusTable = () => {
  const hook = useOrderStatus();
  const theme = useTheme();
  const { t } = useTranslation('commonGeneral');

  return (
    <Paper elevation={3} sx={{ padding: `${theme.spacing(2)} 0 0` }}>
      <Box
        display="flex"
        justifyContent="space-between"
        width="100%"
        height="auto"
        mb={2}
        sx={{
          padding: `0 ${theme.spacing(4)}`,
          flexDirection: { xxs: 'column', xl: 'row' },
          gap: { xxs: '0.5rem', xl: 0 },
          alignItems: 'center',
        }}
      >
        <SearchInput
          buttonText="Buscar"
          placeholder="Buscar por número de orden"
          handleKeyPress={hook.handleKeyPress}
          onChange={hook.onChgSearch}
          onClick={hook.onSearch}
          sx={{ width: '100%' }}
        />
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xxs: 'column', xs: 'row' },
            alignItems: 'center',
            justifyContent: 'center',
            gap: '0.25rem',
          }}
        >
          <Box
            display="flex"
            gap="0.5rem"
            alignItems="center"
            justifyContent="center"
            sx={{
              cursor: 'pointer',
              ':hover': { backgroundColor: theme.palette.primary.lighter },
              padding: '0.5rem',
              borderRadius: '1rem',
            }}
            onClick={hook.handleOpenDrawer}
          >
            <FilterIcon color="primary" />

            <Typography variant="body1" fontWeight={theme.typography.fontWeightMedium}>
              Filtros
            </Typography>
          </Box>

          <Button
            onClick={hook.handleOpenDownloadModal}
            variant="text"
            size="small"
            startIcon={<CloudDownloadOutlinedIcon color="primary" />}
          >
            Descargar órdenes
          </Button>
        </Box>
      </Box>
      <Box sx={{ overflowX: 'auto', width: '100%' }}>
        <Box sx={{ minWidth: '700px' }}>
          <AgaveDataGrid
            columns={orderStatusColumns({
              t,
              handleOpenModal: hook.handleOpenModal,
              downloadOrder: hook.downloadOrderLabel,
            })}
            rows={hook.orders.data}
            localeText={{
              noRowsLabel: hook.hasActiveFilters
                ? 'No encontramos ninguna orden que coincida con tu búsqueda.'
                : 'No hay envíos disponibles aca',
            }}
            rowCount={hook.orders.totalRows}
            rowHeight={74}
            disableRowSelectionOnClick={true}
            onCellClick={(params) => {
              if (!['actions', '__check__'].includes(params.colDef.field)) {
                hook.fetchOrderByTrackingId(params.row.id);
              }
            }}
            getRowId={(row) => row.id}
            hideFooterSelectedRowCount={true}
            paginationModel={{
              page: hook.pagination.filters.page as number,
              pageSize: hook.pagination.filters.page_size as number,
            }}
            disableColumnMenu={true}
            paginationMode="server"
            pageSizeOptions={[5, 10, 25, 50, 100]}
            onPaginationModelChange={hook.pagination.onPaginationModelChange}
          />
        </Box>
      </Box>
      <GenericDrawer
        open={hook.openDrawer}
        onClose={hook.handleOpenDrawer}
        title={'Filtros'}
        disableEscapeKeyDown={true}
        iconTitle={<FilterIcon color="primary" />}
      >
        <Filters
          selectedFilters={hook.filters}
          onChange={hook.onChangeFilter}
          clearFilters={hook.clearFilters}
          applyFilters={hook.applyFilters}
          filters={filters}
        />
      </GenericDrawer>

      <Modal isOpen={hook.openModal} onClose={hook.handleCloseModal} title={'Cancelar orden'}>
        <Typography textAlign={'center'}>
          ¿Estás seguro de que deseas cancelar esta orden?
        </Typography>
        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
          <Button sx={{ marginTop: '1rem' }} onClick={hook.cancelOrder}>
            Sí, cancelar
          </Button>
        </Box>
      </Modal>
      <Modal
        isOpen={hook.openDownloadModal}
        onClose={hook.handleCloseDownloadModal}
        title={'Descargar órdenes'}
      >
        <DownloadModal downloadOrders={hook.downloadOrders} />
      </Modal>
      <DetailsModal
        isOpen={hook.openDetailModal}
        onClose={hook.handleCloseDetailModal}
        orderDetails={hook.orderDetails.data}
        isFFOrder
        map={false}
        isPickups={false}
        isLoading={hook.orderDetails.loading}
        showActivityText="Mostrar productos"
        hideActivityText="Ocultar productos"
      />
    </Paper>
  );
};

export default OrderStatusTable;
